<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue'
import store from '../../store'
import { getTimeAgo } from '../../utils/animate'

const circleRefs = ref<Map<string, HTMLSpanElement | null>>(new Map())

const timeNow = ref(Date.now())
let timer: ReturnType<typeof setInterval> | null = null

onMounted(() => {
  timer = setInterval(() => {
    timeNow.value = Date.now()
  }, 1000)
})

onUnmounted(() => {
  if (timer) clearInterval(timer)
})

const spacesRecentMotion = computed(() => {
  if (!store.activeFloorplan?.spaces) return []

  const result = store.activeFloorplan.spaces
    .map((space) => {
      let mostRecentGroup = null
      let mostRecentTime: Date | null = null
      let mostRecentFeatureId = null

      space.groups.forEach((group) => {
        const featureId = group.pid
        const animFeature = store.getFeatureToAnimate(featureId)
        if (animFeature) {
          const eventTime = new Date(animFeature.lastMotionAt)
          if (!mostRecentTime || eventTime > mostRecentTime) {
            mostRecentTime = eventTime
            mostRecentGroup = group
            mostRecentFeatureId = featureId
          }
        }
      })

      if (mostRecentGroup && mostRecentTime && mostRecentFeatureId) {
        const circleEl = circleRefs.value.get(space.pid)
        if (circleEl) {
          const circleFeatureId = `circle:${space.pid}`
          const sourceFeature = store.getFeatureToAnimate(mostRecentFeatureId)

          if (sourceFeature) {
            store.updateFeaturesToAnimate(circleFeatureId, {
              featureId: circleFeatureId,
              element: circleEl,
              lastMotionAt: sourceFeature.lastMotionAt,
              sourceFeatureId: mostRecentFeatureId,
            })
          }
        }
      }

      return {
        space,
        mostRecentGroup,
        mostRecentTime,
        mostRecentFeatureId,
        timeAgo: mostRecentTime ? getTimeAgo(mostRecentTime, timeNow.value) : null,
      }
    })
    .filter((item) => item.mostRecentTime !== null)
    .sort((a, b) => b.mostRecentTime.getTime() - a.mostRecentTime.getTime())

  return result
})

function setCircleRef(el: HTMLSpanElement | null, spacePid: string) {
  circleRefs.value.set(spacePid, el)
}
</script>

<template>
  <section class="motion-event-list">
    <h2>Last Motion Detected</h2>
    <ul>
      <li v-for="item in spacesRecentMotion" :key="item.space.pid">
        <div class="motion-event">
          <div class="space">
            <i class="pi pi-box" style="margin-right: 4px" />
            <p>{{ item.space.name }}</p>
          </div>
          <div>
            <span class="time-ago">{{ item.timeAgo }}</span>
            <span class="circle-indicator" :ref="(el) => setCircleRef(el as HTMLSpanElement, item.space.pid)"> </span>
          </div>
        </div>
      </li>
    </ul>
    <p v-if="!spacesRecentMotion.length" class="no-info">No info to display</p>
  </section>
</template>

<style scoped>
.motion-event-list {
  padding-bottom: 16px;
}
.motion-event-list li {
  list-style-type: none;
}
.motion-event-list ul {
  padding: 0;
  padding-top: 8px;
  margin: 0;
}
.motion-event-list h2 {
  color: var(--grayscale-800, #49494a);
  font-family: Inter;
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 24px;
}

.motion-event {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.space {
  display: flex;
  align-items: baseline;
}

.time-ago {
  color: var(--global-textSecondaryColor, #6b7280);
  font-family: Inter;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
}

.circle-indicator {
  display: inline-block;
  margin-left: 0.5rem;
  width: 8px;
  height: 8px;
  border-radius: 50%;
}
.no-info {
  color: var(--global-textSecondaryColor, #6b7280);
  font-family: Inter;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  margin: 0;
}
</style>
